import React, { useEffect, useState } from 'react'
import {
  Loader2, FileText, Download, X, CheckCircle2, Clapperboard, ExternalLink,
  Plus, Mic, Search, Eye, ThumbsUp, FolderInput, ChevronDown, RotateCw, Tag, Star, LogIn,
  CheckSquare, Square, Layers, Info, AlertTriangle, StopCircle, SlidersHorizontal, Languages,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useChannels, useChannelVideos, useTranscript, useAddChannel, useIndexVideo, useSetChannelCategory, useFollows, useSetFollow, useMarkSeen, useTiktokLogin, useIndexBatchPreview, useIndexBatchStart, useTranscribeBatchPreview, useTranscribeBatchStart, useCancelSaga, useExpandSearchTerms } from '../hooks/useTranscripts'
import ChannelDiscovery from './ChannelDiscovery'
import { useDocuments, useProjects } from '../hooks/useRag'
import { useSubmitImport, useImportJob, useSaga, TERMINAL, SAGA_TERMINAL } from '../hooks/useImport'
import { useDefaults } from '../hooks/useSettings'
import { toParagraphs, sourceLink } from './docFormat'
import { canEmbed } from './VideoPlayer'
import usePlayerStore from '../stores/playerStore'

const FILTERS = [
  ['transcribed', 'Transcribed'],
  ['all', 'All'],
  ['not_transcribed', 'Not transcribed'],
]
const FORMATS = [
  ['text', 'Text'],
  ['srt', 'SRT'],
  ['words', 'Words'],
]

const fmtDur = (s) => {
  if (!s && s !== 0) return ''
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = String(Math.round(s % 60)).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

const fmtDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d) ? String(iso).slice(0, 10) : d.toLocaleDateString()
}

const fmtCount = (n) => {
  if (n == null || n === '') return null
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`
  return String(n)
}

// Relative age reads faster than a date when scanning for fresh/stale content;
// the exact date stays in the tooltip.
const fmtAge = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d)) return null
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000))
  if (days < 1) return 'today'
  if (days < 30) return `${days} d`
  if (days < 365) return `${Math.floor(days / 30)} mo`
  const y = Math.floor(days / 365)
  const mo = Math.floor((days % 365) / 30)
  return mo ? `${y} y ${mo} mo` : `${y} y`
}

const SORTS = [
  ['newest', 'Newest'],
  ['oldest', 'Oldest'],
  ['views', 'Most viewed'],
  ['likes', 'Most liked'],
  ['duration', 'Longest'],
  ['title', 'Title A–Z'],
]

// The artifact filename a video's title produces (transcribe sanitizes Windows-
// forbidden characters to '_'). Used to detect "already indexed in the project"
// by matching the project's document basenames — the Index action always
// indexes these channel artifacts, so the name is deterministic.
const sanitizeStem = (title) => String(title || '').replace(/[<>:"/\\|?*]/g, '_').trim()
const docBasename = (p) => String(p || '').split(/[\\/]/).pop()

// Recognize a pasted channel URL and derive {source, channel_id} from it, so
// users can paste either "@handle" or the full channel link.
function parseChannelInput(raw) {
  const s = String(raw || '').trim()
  let m = s.match(/youtube\.com\/(@[\w.-]+)/i)
  if (m) return { source: 'youtube', channel_id: m[1] }
  m = s.match(/tiktok\.com\/(@[\w.-]+)/i)
  if (m) return { source: 'tiktok', channel_id: m[1] }
  m = s.match(/rutube\.ru\/channel\/(\d+)/i)
  if (m) return { source: 'rutube', channel_id: m[1] }
  return null
}

// The transcript desk: browse transcribe's channels → videos → read & export the
// transcript (verify with SRT / word-level timestamps). Channel-oriented, so it
// sits outside the project-scoped bar.
const PAGE = 200

export default function TranscriptsLibrary({ project, onFullPipeline }) {
  const channelsQ = useChannels()
  const [channel, setChannel] = useState('')
  const [category, setCategory] = useState('')
  const [filter, setFilter] = useState('transcribed')
  const [offset, setOffset] = useState(0)
  const [sort, setSort] = useState('newest')
  // Length window, in MINUTES because that is how anyone picking items thinks
  // about it ("only the short ones"). 'any' leaves the listing untouched.
  const [lenDir, setLenDir] = useState('any')   // any | under | over
  const [lenMin, setLenMin] = useState(5)
  const [search, setSearch] = useState('')
  // Cross-language search: the query expanded into the words a title might use
  // for the same topic. Held as visible, removable terms rather than applied
  // behind the scenes — otherwise results look arbitrary and cannot be corrected.
  const [terms, setTerms] = useState([])
  const [termNote, setTermNote] = useState(null)
  const expandTerms = useExpandSearchTerms()
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search)
      setOffset(0)
    }, 300)
    return () => clearTimeout(t)
  }, [search])
  // Editing the query invalidates an expansion built from the old one — keeping
  // it would quietly search for something the box no longer says.
  useEffect(() => { setTerms([]); setTermNote(null) }, [search, channel, category])
  const lenValue = Number(lenMin) > 0 ? Number(lenMin) : null
  const videosQ = useChannelVideos({
    channelId: channel, category, filter, limit: PAGE, sort, offset,
    // Terms REPLACE the plain query rather than narrowing it: the engine ANDs
    // the two, so sending both would demand the original word be present and
    // undo the point of searching across languages.
    search: terms.length ? '' : debounced,
    terms: terms.length ? terms : undefined,
    minMinutes: lenDir === 'over' && lenValue ? lenValue : undefined,
    maxMinutes: lenDir === 'under' && lenValue ? lenValue : undefined,
  })
  const [openVideo, setOpenVideo] = useState(null)

  const [addOpen, setAddOpen] = useState(false)
  // Surgical selection (secondary to the scope path) — ids of ticked rows.
  const [selected, setSelected] = useState(() => new Set())
  const clearSel = () => setSelected(new Set())
  const toggleSel = (id) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const channels = (channelsQ.data?.channels ?? []).filter((c) => !String(c.id).startsWith('.'))
  const videos = videosQ.data?.videos ?? []
  const total = videosQ.data?.total ?? 0
  // Items whose length is unknown cannot be judged against the window, so the
  // engine drops them — and reports how many. Say so rather than let them
  // vanish: "where did my videos go" is exactly how the last two list bugs read.
  const lenUnknown = videosQ.data?.duration_filter?.unknown_excluded ?? 0

  // Which videos are already indexed in the selected project? Match the
  // project's document basenames against each title's artifact names.
  const docsQ = useDocuments(project)
  const indexedNames = React.useMemo(
    () => new Set((docsQ.data?.documents ?? []).map((d) => docBasename(d.source))),
    [docsQ.data]
  )
  const isIndexed = (title) => {
    const stem = sanitizeStem(title)
    return indexedNames.has(`${stem}_subtitles.srt`) || indexedNames.has(`${stem}_transcript.txt`)
  }
  // followed channels: "N new since last visit" badges; opening a channel
  // marks it seen (resets its counter to the current video_count)
  const followsQ = useFollows()
  const follows = React.useMemo(() => {
    const m = new Map()
    for (const f of followsQ.data?.follows ?? []) m.set(f.channel, f)
    return m
  }, [followsQ.data])
  const seen = useMarkSeen()
  const newCount = (c) => {
    const f = follows.get(c.id)
    if (!f || c.video_count == null) return 0
    return Math.max(0, c.video_count - (f.last_seen_total ?? 0))
  }

  const pickChannel = (id) => {
    setChannel(id); setCategory(''); setOffset(0); setOpenVideo(null); clearSel()
    const c = channels.find((x) => x.id === id)
    if (id && follows.has(id) && c?.video_count != null) {
      seen.mutate({ channel: id, video_count: c.video_count })
    }
  }
  const pickCategory = (c) => { setCategory(c); setChannel(''); setOffset(0); setOpenVideo(null); clearSel() }
  const pickFilter = (f) => { setFilter(f); setOffset(0); clearSel() }
  // Same reset as a status change: the page and any ticked rows refer to a list
  // that no longer exists.
  const pickLength = (dir, minutes) => {
    setLenDir(dir); if (minutes != null) setLenMin(minutes)
    setOffset(0); clearSel()
  }
  const categories = React.useMemo(
    () => [...new Set(channels.map((c) => c.category).filter(Boolean))].sort(),
    [channels]
  )
  const active = channel || category

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={channel}
          onChange={(e) => pickChannel(e.target.value)}
          className="h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        >
          <option value="">{channelsQ.isLoading ? 'Loading channels…' : `Select a channel… (${channels.length})`}</option>
          {channels.map((c) => (
            <option key={c.id} value={c.id}>
              {follows.has(c.id) ? '★ ' : ''}{c.id}{c.category ? ` [${c.category}]` : ''}
              {c.video_count != null ? ` (${c.video_count})` : ''}
              {newCount(c) > 0 ? ` — ${newCount(c)} new` : ''}
            </option>
          ))}
        </select>

        {categories.length > 0 && (
          <select
            value={category}
            onChange={(e) => pickCategory(e.target.value)}
            className="h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
            title="Browse all channels of a category at once (multichannel)"
          >
            <option value="">…or a category</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                ⊞ {c} ({channels.filter((x) => x.category === c).length} channels)
              </option>
            ))}
          </select>
        )}

        {channel && <RefreshChannelButton key={channel} channelInfo={channels.find((c) => c.id === channel)} />}
        {channel && (
          <CategoryEditor
            key={`cat-${channel}`}
            channel={channel}
            current={channels.find((c) => c.id === channel)?.category}
            suggestions={categories}
          />
        )}
        {channel && (
          <FollowButton
            key={`fw-${channel}`}
            channelInfo={channels.find((c) => c.id === channel)}
            follow={follows.get(channel)}
          />
        )}

        {active && (
          <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
            {FILTERS.map(([f, label]) => (
              <button
                key={f}
                onClick={() => pickFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs transition ${
                  filter === f ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {active && (
          <>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setOffset(0) }}
              className="h-9 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              title="Sort videos"
            >
              {SORTS.map(([s, label]) => (
                <option key={s} value={s}>{label}</option>
              ))}
            </select>
            {/* Length window. Minutes, because that is the unit anyone picking
                items for transcription thinks in; the BFF converts to seconds. */}
            <div className="flex items-center gap-1">
              <select
                value={lenDir}
                onChange={(e) => pickLength(e.target.value)}
                className="h-9 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
                title="Filter by item length"
              >
                <option value="any">Any length</option>
                <option value="under">Under</option>
                <option value="over">Over</option>
              </select>
              {lenDir !== 'any' && (
                <>
                  <input
                    type="number"
                    min="1"
                    value={lenMin}
                    onChange={(e) => pickLength(lenDir, e.target.value)}
                    className="h-9 w-14 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
                    title="Length in minutes"
                  />
                  <span className="text-xs text-ink-soft dark:text-mist/50">min</span>
                </>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-soft dark:text-mist/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search titles…"
                className="h-9 w-48 rounded-lg pl-8 pr-2 text-xs bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              />
            </div>
            {/* Titles in one channel are mixed Russian / English / Czech, and a
                substring can never bridge that. One click expands the query
                into the words a title might actually use. */}
            <button
              type="button"
              disabled={!search.trim() || expandTerms.isPending}
              title="Also find this topic in the channel's other languages"
              onClick={() => {
                const sampleTitles = videos.slice(0, 25).map((v) => v.title).filter(Boolean)
                expandTerms.mutate(
                  { query: search.trim(), sampleTitles },
                  {
                    onSuccess: (r) => {
                      setTerms(r.terms || [])
                      setTermNote(r.degraded ? 'Expansion unavailable — searching the plain query.' : null)
                      setOffset(0)
                      clearSel()
                    },
                  }
                )
              }}
              className="inline-flex items-center gap-1 h-9 px-2.5 rounded-lg text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
            >
              <Languages className="w-3.5 h-3.5" />
              {expandTerms.isPending ? 'Expanding…' : 'All languages'}
            </button>
          </>
        )}

        <button
          onClick={() => setAddOpen((v) => !v)}
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs border border-gold/50 text-gold-deep hover:bg-gold/10 dark:text-gold"
        >
          <Plus className="w-3.5 h-3.5" /> Add channel
        </button>
      </div>

      {addOpen && (
        <AddChannelForm
          onAdded={(id) => {
            setAddOpen(false)
            pickChannel(id)
            // a fresh channel has nothing transcribed yet — show all its videos
            setFilter('all')
          }}
        />
      )}

      {channelsQ.isError && <p className="text-sm text-red-600">{channelsQ.error?.message}</p>}

      {active && (
        <BatchTranscribeBar channel={channel} category={category} />
      )}

      {active && project && (
        <BatchIndexBar
          project={project}
          channel={channel}
          category={category}
          selectedVideos={videos.filter((v) => selected.has(v.id))}
          transcribedShown={videos.filter((v) => v.is_transcribed)}
          onSelectAll={() => setSelected(new Set(videos.filter((v) => v.is_transcribed).map((v) => v.id)))}
          onClearSel={clearSel}
        />
      )}

      {!active && (
        <div className="rounded-xl border border-dashed border-parchment-border p-6 text-center text-ink-soft dark:border-midnight-600 dark:text-mist/50">
          <Clapperboard className="w-8 h-8 mx-auto mb-2 opacity-50" />
          Pick a channel to browse its videos and read their transcripts.
        </div>
      )}

      {active && (
        <>
          {videosQ.isLoading && (
            <div className="flex items-center gap-2 text-sm text-ink-soft dark:text-mist/60">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading videos…
            </div>
          )}
          {videosQ.isError && <p className="text-sm text-red-600">{videosQ.error?.message}</p>}
          {!videosQ.isLoading && videos.length === 0 && (
            <p className="text-sm text-ink-soft dark:text-mist/50 italic">No videos match this filter.</p>
          )}

          {/* no overflow-hidden on the list: the Transcribe ▾ popover must be able
              to extend past a short list; rounded corners live on first/last row */}
          {videos.length > 0 && (
            <ul className="divide-y divide-parchment-border dark:divide-midnight-600 rounded-xl border border-parchment-border dark:border-midnight-600">
              {videos.map((v) => (
                <li
                  key={v.id}
                  className="flex items-center gap-3 px-4 py-2.5 bg-white/60 dark:bg-midnight-800/50 first:rounded-t-xl last:rounded-b-xl"
                  title={v.description || undefined}
                >
                  {/* batch selection — only transcribed rows can be indexed */}
                  {project && v.is_transcribed ? (
                    <button
                      onClick={() => toggleSel(v.id)}
                      className="shrink-0 text-ink-soft hover:text-gold-deep dark:text-mist/50 dark:hover:text-gold"
                      title={selected.has(v.id) ? 'Deselect' : 'Select for batch indexing'}
                    >
                      {selected.has(v.id)
                        ? <CheckSquare className="w-4 h-4 text-gold-deep dark:text-gold" />
                        : <Square className="w-4 h-4" />}
                    </button>
                  ) : project ? (
                    <span className="w-4 h-4 shrink-0" />
                  ) : null}
                  <Thumb src={v.thumbnail} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink dark:text-mist truncate" title={v.title}>
                      {v.title}
                      {v.is_short && (
                        <span className="ml-2 align-middle px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-spark/15 text-spark-600 dark:text-spark">short</span>
                      )}
                      {v.is_live && (
                        <span className="ml-2 align-middle px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">live</span>
                      )}
                    </div>
                    <div className="text-xs text-ink-soft dark:text-mist/50 flex items-center gap-2.5">
                      {category && v.channel_title && (
                        <span
                          className="px-1.5 py-0.5 rounded bg-gold/10 text-gold-deep dark:text-gold truncate max-w-[160px]"
                          title={v.channel}
                        >
                          {v.channel_title}
                        </span>
                      )}
                      {v.duration != null && v.duration !== '' && <span>{fmtDur(v.duration)}</span>}
                      {v.published && (
                        <span title={fmtDate(v.published)}>{fmtAge(v.published) ?? fmtDate(v.published)}</span>
                      )}
                      {fmtCount(v.views) && (
                        <span className="inline-flex items-center gap-0.5">
                          <Eye className="w-3 h-3" /> {fmtCount(v.views)}
                        </span>
                      )}
                      {fmtCount(v.likes) && (
                        <span className="inline-flex items-center gap-0.5">
                          <ThumbsUp className="w-3 h-3" /> {fmtCount(v.likes)}
                        </span>
                      )}
                      <VideoSourceLink video={v} />
                    </div>
                  </div>
                  {v.is_transcribed ? (
                    <>
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 className="w-3.5 h-3.5" /> transcribed
                      </span>
                      {project && isIndexed(v.title) ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-gold-deep dark:text-gold"
                          title={`Already indexed in project "${project}"`}
                        >
                          <FolderInput className="w-3.5 h-3.5" /> in {project}
                        </span>
                      ) : (
                        <IndexVideoButton video={v} project={project} />
                      )}
                      <button
                        onClick={() => setOpenVideo({ id: v.id, title: v.title })}
                        className="px-2.5 py-1 rounded-md text-xs border border-gold/50 text-gold-deep hover:bg-gold/10 dark:text-gold"
                      >
                        View transcript
                      </button>
                    </>
                  ) : (
                    <TranscribeButton video={v} channel={v.channel || channel} onFullPipeline={onFullPipeline} />
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* What the search actually looked for. Visible and removable: an
              expansion you cannot see or correct is worse than none. */}
          {(terms.length > 0 || termNote) && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {terms.length > 0 && (
                <span className="text-ink-soft dark:text-mist/50">Searching for:</span>
              )}
              {terms.map((t) => (
                <button
                  key={t}
                  onClick={() => { setTerms(terms.filter((x) => x !== t)); setOffset(0); clearSel() }}
                  title="Remove this term"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gold/40 text-gold-deep hover:bg-gold/10 dark:text-gold"
                >
                  {t} <X className="w-3 h-3" />
                </button>
              ))}
              {terms.length > 0 && (
                <button
                  onClick={() => { setTerms([]); setOffset(0); clearSel() }}
                  className="text-ink-soft underline hover:text-gold-deep dark:text-mist/50 dark:hover:text-gold"
                >
                  back to plain search
                </button>
              )}
              {termNote && <span className="text-amber-600 dark:text-amber-400">{termNote}</span>}
            </div>
          )}

          {videos.length > 0 && (
            <div className="flex items-center justify-between text-xs text-ink-soft dark:text-mist/50">
              <span>
                {total > 0 ? `${offset + 1}–${offset + videos.length}` : '0'} of {total}
                {filter !== 'all' && ` ${filter === 'transcribed' ? 'transcribed' : 'not transcribed'}`}
                {lenDir !== 'any' && lenValue ? ` · ${lenDir} ${lenValue} min` : ''}
                {lenUnknown > 0 && (
                  <span title="Their length is not known yet — a metadata refresh fills it in.">
                    {' '}· {lenUnknown} hidden (no length)
                  </span>
                )}
                {(() => {
                  const channelTotal = channels.find((c) => c.id === channel)?.video_count
                  return filter !== 'all' && channelTotal > total ? (
                    <>
                      {' · '}
                      <button onClick={() => pickFilter('all')} className="underline hover:text-gold-deep dark:hover:text-gold">
                        {channelTotal} in channel — show all
                      </button>
                    </>
                  ) : null
                })()}
                {videosQ.isFetching && <Loader2 className="inline w-3 h-3 ml-1.5 animate-spin" />}
              </span>
              {total > PAGE && (
                <span className="inline-flex gap-2">
                  <button
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
                    disabled={offset === 0 || videosQ.isFetching}
                    className="px-3 py-1.5 rounded-md border border-parchment-border disabled:opacity-40 hover:border-gold dark:border-midnight-600"
                  >Prev</button>
                  <button
                    onClick={() => setOffset((o) => o + PAGE)}
                    disabled={offset + PAGE >= total || videosQ.isFetching}
                    className="px-3 py-1.5 rounded-md border border-parchment-border disabled:opacity-40 hover:border-gold dark:border-midnight-600"
                  >Next</button>
                </span>
              )}
            </div>
          )}
        </>
      )}

      {openVideo && <TranscriptPanel video={openVideo} onClose={() => setOpenVideo(null)} />}
    </div>
  )
}

// Tag the selected channel with a content category. Categories group channels
// into multichannel listings (cross-channel search → multichannel research
// projects), so the tag is a first-class control next to the picker.
function CategoryEditor({ channel, current, suggestions }) {
  const setCat = useSetChannelCategory()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(current || '')
  const save = () => setCat.mutate({ channel, category: value.trim() }, { onSuccess: () => setEditing(false) })

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
        title="Channel category — groups channels for multichannel listings"
      >
        <Tag className="w-3.5 h-3.5" />
        {current || 'category'}
      </button>
    )
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input
        autoFocus
        list="veralis-categories"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        placeholder="e.g. AI"
        className="h-9 w-32 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
      />
      <datalist id="veralis-categories">
        {suggestions.map((s) => <option key={s} value={s} />)}
      </datalist>
      <button
        onClick={save}
        disabled={setCat.isPending}
        className="h-9 px-2 rounded-md text-xs border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-50 dark:text-gold"
      >
        {setCat.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'set'}
      </button>
    </span>
  )
}

// Follow a channel → a per-channel transcribe→index pipeline: the BFF polls it
// (incremental refresh + detail enrichment) and, per the automation config,
// transcribes new videos (captions or captions+ASR) and indexes them into a
// chosen project; an opt-in backfill processes the existing archive.
const BACKFILL_ACTIVE = ['pending', 'running']

function FollowConfig({ follow, base, setFollow }) {
  const { data: projData } = useProjects()
  const projects = projData?.projects ?? []
  const [limit, setLimit] = useState('')
  const mode = follow?.auto_transcribe ?? 'off'
  const project = follow?.project ?? ''
  const backfill = follow?.backfill ?? 'off'
  const backfillActive = BACKFILL_ACTIVE.includes(backfill)
  // send the full current config + the patch (followChannel merges server-side)
  const save = (patch) =>
    setFollow.mutate({ ...base, auto_transcribe: mode, project, ...patch })
  const fieldCls =
    'w-full h-8 rounded-md px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'
  return (
    <div className="absolute z-30 top-10 right-0 w-72 rounded-lg border border-parchment-border bg-white p-3 shadow-lg space-y-3 dark:bg-midnight-800 dark:border-midnight-600">
      <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50">Automation</div>

      <label className="block space-y-1">
        <span className="text-xs text-ink-soft dark:text-mist/60">Transcribe new videos</span>
        <select value={mode} onChange={(e) => save({ auto_transcribe: e.target.value })} className={fieldCls}
          title="captions = free platform captions only (no audio, no ASR); captions+ASR falls back to Whisper when a video has no captions (needed for RuTube/TikTok).">
          <option value="off">off</option>
          <option value="captions">captions only (free)</option>
          <option value="captions_asr">captions + ASR fallback</option>
        </select>
      </label>

      {mode !== 'off' && (
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Index into project</span>
          <select value={project} onChange={(e) => save({ project: e.target.value })} className={fieldCls}>
            <option value="">— transcribe only (don’t index) —</option>
            {projects.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </label>
      )}

      {project && (
        <div className="space-y-1 pt-1 border-t border-parchment-border/60 dark:border-midnight-600/60">
          <span className="text-xs text-ink-soft dark:text-mist/60">Backfill the archive</span>
          {backfillActive ? (
            <div className="flex items-center gap-2 text-xs text-gold-deep dark:text-gold">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> backfilling… (see Activity)
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="newest N (blank = all)" className={`${fieldCls} flex-1`} />
              <button onClick={() => save({ backfill: 'pending', backfill_limit: limit ? Number(limit) : '' })}
                disabled={setFollow.isPending}
                className="h-8 px-2.5 rounded-md text-xs border border-gold/60 bg-gold/10 text-gold-deep dark:text-gold disabled:opacity-50">
                Backfill
              </button>
            </div>
          )}
          {backfill === 'done' && <div className="text-[11px] text-emerald-600 dark:text-emerald-400">✓ archive backfilled</div>}
          {backfill === 'error' && <div className="text-[11px] text-red-600">backfill failed — retry</div>}
          <p className="text-[11px] text-ink-soft dark:text-mist/40">captions+ASR downloads audio &amp; runs Whisper — can be slow/costly on a big archive.</p>
        </div>
      )}
    </div>
  )
}

function FollowButton({ channelInfo, follow }) {
  const setFollow = useSetFollow()
  const [open, setOpen] = useState(false)
  if (!channelInfo?.source || !channelInfo?.channel_id) return null
  const followed = !!follow
  const base = {
    channel: channelInfo.id, followed: true,
    source: channelInfo.source, channel_id: channelInfo.channel_id,
  }
  const toggle = () =>
    setFollow.mutate({ ...base, followed: !followed, video_count: channelInfo.video_count,
      auto_transcribe: follow?.auto_transcribe ?? 'off' })
  const active = follow && (follow.auto_transcribe !== 'off' || follow.project)
  return (
    <span className="relative inline-flex items-center gap-1">
      <button
        onClick={toggle}
        disabled={setFollow.isPending}
        className={`inline-flex items-center gap-1 h-9 px-2.5 rounded-md text-xs border transition disabled:opacity-50 ${
          followed
            ? 'border-gold/60 bg-gold/10 text-gold-deep dark:text-gold'
            : 'border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold'
        }`}
        title={followed
          ? 'Followed — checked for new videos twice a day. Click to unfollow.'
          : 'Follow: auto-check for new videos twice a day'}
      >
        <Star className={`w-3.5 h-3.5 ${followed ? 'fill-current' : ''}`} />
        {followed ? 'following' : 'follow'}
      </button>
      {followed && (
        <button
          onClick={() => setOpen((o) => !o)}
          className={`inline-flex items-center gap-1 h-9 px-2 rounded-md text-xs border transition ${
            active
              ? 'border-gold/60 bg-gold/10 text-gold-deep dark:text-gold'
              : 'border-parchment-border text-ink-soft hover:border-gold dark:border-midnight-600 dark:text-mist/60'
          }`}
          title="Automation: transcribe mode, target project, backfill"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          <ChevronDown className={`w-3 h-3 transition ${open ? 'rotate-180' : ''}`} />
        </button>
      )}
      {followed && open && <FollowConfig follow={follow} base={base} setFollow={setFollow} />}
    </span>
  )
}

// Channels aren't static — pull NEW videos into an existing channel's metadata.
// Same endpoint as Add channel; all three platform downloaders are incremental
// (fetch only unknown ids, merge with the existing list — nothing is lost).
function RefreshChannelButton({ channelInfo }) {
  const refresh = useAddChannel()
  if (!channelInfo || !channelInfo.source || !channelInfo.channel_id) return null
  const cap = { youtube: true, rutube: true, tiktok: true }[channelInfo.source]
  if (!cap) return null // local/drive channels have no metadata API
  return (
    <button
      onClick={() =>
        refresh.mutate({ source: channelInfo.source, channel_id: channelInfo.channel_id })
      }
      disabled={refresh.isPending}
      title={
        refresh.isError
          ? refresh.error?.message
          : refresh.isSuccess
            ? `Updated — channel now has ${refresh.data?.videos_fetched ?? '?'} videos`
            : 'Check the channel for new videos'
      }
      className={`inline-flex items-center gap-1 h-9 px-2.5 rounded-md text-xs border transition disabled:opacity-50 ${
        refresh.isError
          ? 'border-red-300 text-red-600'
          : 'border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold'
      }`}
    >
      <RotateCw className={`w-3.5 h-3.5 ${refresh.isPending ? 'animate-spin' : ''}`} />
      {refresh.isPending ? 'checking…' : refresh.isSuccess ? 'updated' : 'refresh'}
    </button>
  )
}

// Row thumbnail with a real fallback: dead CDN images (removed channels) leave
// an empty box with plain onError-hiding — track the failure and show the icon.
function Thumb({ src }) {
  const [failed, setFailed] = useState(false)
  if (!src || failed) {
    return <FileText className="w-4 h-4 shrink-0 text-ink-soft dark:text-mist/50" />
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className="w-20 h-11 shrink-0 rounded-md object-cover border border-parchment-border dark:border-midnight-600"
      onError={() => setFailed(true)}
    />
  )
}

// "source" affordance on a video row: embedded player when the platform
// supports it (YouTube/Rutube/TikTok), external tab otherwise (local/drive
// uploads, audio_url, …).
function VideoSourceLink({ video }) {
  const openPlayer = usePlayerStore((s) => s.openPlayer)
  const meta = {
    source_url: video.video_url,
    origin: video.origin_type,
    video_id: video.id,
    title: video.title,
  }
  if (!video.video_url) return null
  if (canEmbed(meta)) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          openPlayer({ ...meta, start: 0 })
        }}
        title="Watch the video here"
        className="inline-flex items-center gap-0.5 hover:text-gold-deep dark:hover:text-gold"
      >
        <Clapperboard className="w-3 h-3" /> watch
      </button>
    )
  }
  return (
    <a
      href={video.video_url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-0.5 hover:text-gold-deep dark:hover:text-gold"
      onClick={(e) => e.stopPropagation()}
    >
      <ExternalLink className="w-3 h-3" /> source
    </a>
  )
}

const SOURCES = [
  ['youtube', 'YouTube', '@handle'],
  ['rutube', 'RuTube', 'channel id (39497707)'],
  ['tiktok', 'TikTok', '@user'],
]

// Register a channel by handle/id — or paste the channel URL and we detect both.
function AddChannelForm({ onAdded }) {
  const add = useAddChannel()
  const login = useTiktokLogin()
  const [source, setSource] = useState('youtube')
  const [ident, setIdent] = useState('')
  const [maxVideos, setMaxVideos] = useState(200)
  // TikTok bot-walled this profile — the fetch came back with 0 videos and a
  // needs_auth flag. Offer sign-in; on success re-run the same fetch.
  const needsAuth = add.isSuccess && add.data?.needs_auth

  const onIdent = (val) => {
    const parsed = parseChannelInput(val)
    if (parsed) {
      setSource(parsed.source)
      setIdent(parsed.channel_id)
    } else {
      setIdent(val)
    }
  }

  const submit = () =>
    ident.trim() &&
    add.mutate(
      { source, channel_id: ident.trim(), max_videos: Number(maxVideos) || undefined },
      { onSuccess: (r) => r?.channel_id && onAdded?.(r.channel_id) }
    )

  return (
    <div className="rounded-xl border border-gold/40 bg-white/70 p-4 dark:border-gold/30 dark:bg-midnight-800/60 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="h-9 rounded-lg px-2 text-sm bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        >
          {SOURCES.map(([s, label]) => (
            <option key={s} value={s}>{label}</option>
          ))}
        </select>
        <input
          value={ident}
          onChange={(e) => onIdent(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder={`${SOURCES.find(([s]) => s === source)?.[2]} — or paste the channel URL`}
          className="h-9 flex-1 min-w-[220px] rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        />
        <label className="text-xs text-ink-soft dark:text-mist/50">
          max videos{' '}
          <input
            type="number"
            min="1"
            value={maxVideos}
            onChange={(e) => setMaxVideos(e.target.value)}
            className="w-20 h-9 rounded-lg px-2 text-sm bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          />
        </label>
        <button
          onClick={submit}
          disabled={add.isPending || !ident.trim()}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
        >
          {add.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Fetch channel
        </button>
      </div>
      {add.isPending && (
        <p className="text-xs text-ink-soft dark:text-mist/50">
          Fetching channel metadata — for large channels this can take a few minutes…
        </p>
      )}
      {add.isError && <p className="text-xs text-red-600">{add.error?.message}</p>}

      {/* Bot-wall → sign-in. Opens a real Chrome window (driven by us) so the
          user logs into TikTok once; cookies are captured and the fetch retried. */}
      {needsAuth && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2 dark:border-amber-700/40 dark:bg-amber-900/20">
          <p className="text-xs text-amber-800 dark:text-amber-200">
            {add.data.hint || 'TikTok is blocking anonymous access to this profile.'}{' '}
            Sign in once and we’ll list its videos.
          </p>
          <button
            onClick={() => login.mutate({}, { onSuccess: (r) => { if (r?.logged_in) submit() } })}
            disabled={login.isPending}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm border border-amber-400 text-amber-800 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-200 dark:border-amber-600/50 dark:hover:bg-amber-900/40"
          >
            {login.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {login.isPending ? 'A Chrome window opened — sign in there…' : 'Sign in to TikTok'}
          </button>
          {login.isError && <p className="text-xs text-red-600">{login.error?.message}</p>}
          {login.isSuccess && !login.data?.logged_in && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Sign-in didn’t complete (timed out). Click to try again.
            </p>
          )}
        </div>
      )}

      <ChannelDiscovery onAdded={onAdded} />
    </div>
  )
}

// Batch transcript indexing bar (scope + delta). Primary path at channel
// scale (5,900+ transcripts): pick a scope, the server subtracts what is
// already indexed, then a durable saga walks the remainder with live progress.
// Ticking rows switches it to the surgical (explicit) mode. Only two artifacts
// are offered — SRT (timestamped, ▶ seek from a citation) and plain text;
// `words`/`detailed` are timing/metadata, not indexable content.
const ARTIFACTS = [
  ['subtitles', 'SRT (with timestamps)'],
  ['transcript', 'Plain text'],
]

function BatchIndexBar({ project, channel, category, selectedVideos, transcribedShown, onSelectAll, onClearSel }) {
  const qc = useQueryClient()
  const [artifact, setArtifact] = useState('subtitles')
  const [preview, setPreview] = useState(null)   // { transcribed, already, remaining, sample }
  const [sagaId, setSagaId] = useState(null)
  const previewM = useIndexBatchPreview()
  const startM = useIndexBatchStart()
  const saga = useSaga(sagaId)

  const surgical = selectedVideos.length > 0
  const scopeArgs = surgical
    ? { video_ids: selectedVideos.map((v) => ({ id: v.id, title: v.title })) }
    : { channel_id: channel || undefined, category: channel ? undefined : category }

  // artifact / selection changes invalidate a stale preview
  useEffect(() => { setPreview(null) }, [artifact, surgical, selectedVideos.length, channel, category])

  const doPreview = () =>
    previewM.mutate({ project, artifact, ...scopeArgs }, { onSuccess: setPreview })

  const doStart = () =>
    startM.mutate({ project, artifact, ...scopeArgs }, { onSuccess: (r) => { setSagaId(r.saga_id); setPreview(null) } })

  // saga finished → refresh the project's documents so "in {project}" badges flip
  const status = saga.data?.status
  const done = status && SAGA_TERMINAL.has(status)
  useEffect(() => {
    if (done) {
      qc.invalidateQueries({ queryKey: ['rag', 'documents', project] })
      qc.invalidateQueries({ queryKey: ['rag', 'projects'] })
    }
  }, [done]) // eslint-disable-line react-hooks/exhaustive-deps

  const steps = saga.data?.steps ?? []
  const resolveStep = steps.find((s) => s.name === 'resolve')
  const indexStep = steps.find((s) => s.name === 'index')
  const running = sagaId && !done
  const label = surgical
    ? `${selectedVideos.length} selected`
    : channel ? `channel ${channel}` : `category ${category}`

  return (
    <div className="rounded-xl border border-gold/30 bg-gold/[0.04] dark:border-gold/20 dark:bg-gold/[0.03] px-4 py-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Layers className="w-4 h-4 text-gold-deep dark:text-gold shrink-0" />
        <span className="text-ink dark:text-mist">
          Batch index <span className="font-medium">{label}</span> → <span className="font-medium">{project}</span>
        </span>

        <label className="inline-flex items-center gap-1.5 text-xs text-ink-soft dark:text-mist/60">
          Import:
          <select
            value={artifact}
            onChange={(e) => setArtifact(e.target.value)}
            disabled={running}
            className="h-8 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist disabled:opacity-50"
          >
            {ARTIFACTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span title="Only the subtitle/plain-text transcript is indexed. Word-level timing (words) and the provider header (detailed) are not.">
            <Info className="w-3.5 h-3.5 opacity-60" />
          </span>
        </label>

        <div className="ml-auto inline-flex items-center gap-2">
          {surgical ? (
            <button onClick={onClearSel} className="text-xs text-ink-soft hover:text-ink dark:text-mist/50">clear</button>
          ) : (
            transcribedShown.length > 0 && (
              <button onClick={onSelectAll} className="text-xs text-ink-soft hover:text-gold-deep dark:text-mist/50 dark:hover:text-gold">
                select {transcribedShown.length} shown
              </button>
            )
          )}
          {!preview && !running && !done && (
            <button
              onClick={doPreview}
              disabled={previewM.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-gold/15 text-gold-deep hover:bg-gold/25 dark:text-gold disabled:opacity-50"
            >
              {previewM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
              {previewM.isPending ? 'Counting…' : 'Preview'}
            </button>
          )}
        </div>
      </div>

      {previewM.isError && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5" /> {previewM.error?.message}
        </p>
      )}

      {/* delta preview → confirm */}
      {preview && !running && !done && (
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-ink-soft dark:text-mist/60">
            <b className="text-ink dark:text-mist">{preview.transcribed}</b> transcribed ·{' '}
            <b className="text-ink dark:text-mist">{preview.already}</b> already in {project} ·{' '}
            <b className="text-gold-deep dark:text-gold">{preview.remaining}</b> to index
          </span>
          <button
            onClick={doStart}
            disabled={preview.remaining === 0 || startM.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-gold text-midnight-900 hover:bg-gold-deep disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {startM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />}
            {preview.remaining === 0 ? 'Nothing to index' : `Index ${preview.remaining} → ${project}`}
          </button>
          <button onClick={() => setPreview(null)} className="text-ink-soft hover:text-ink dark:text-mist/50">dismiss</button>
        </div>
      )}

      {/* live saga progress */}
      {sagaId && (
        <div className="text-xs space-y-1">
          {!done && resolveStep?.status !== 'done' && (
            <p className="text-ink-soft dark:text-mist/60 flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving delta…
            </p>
          )}
          {(running || done) && indexStep && (() => {
            const d = indexStep.data ?? {}
            const total = d.total ?? resolveStep?.data?.total ?? 0
            const cur = (d.indexed ?? 0) + (d.skipped ?? 0)
            const pct = total ? Math.round((cur / total) * 100) : (done ? 100 : 0)
            return (
              <>
                <div className="h-1.5 rounded-full bg-parchment-border dark:bg-midnight-600 overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-ink-soft dark:text-mist/60">
                  {done ? 'Done — ' : `Indexing… ${cur}/${total} · `}
                  indexed <b className="text-ink dark:text-mist">{d.indexed ?? 0}</b>
                  {(d.chunks ?? 0) > 0 && <> ({d.chunks} chunks)</>}
                  {(d.skipped ?? 0) > 0 && <> · skipped {d.skipped}</>}
                  {(d.failed ?? 0) > 0 && <> · <span className="text-red-600">failed {d.failed}</span></>}
                </p>
              </>
            )
          })()}
          {status === 'failed' && (
            <p className="text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {saga.data?.error}</p>
          )}
          {done && (
            <button onClick={() => { setSagaId(null); onClearSel() }} className="text-gold-deep dark:text-gold hover:underline">
              done — dismiss
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Bulk transcription bar (scope + delta) — plan-transcribe-parity Step 1, the
// capability that used to force the tkinter GUI open. Pick a channel/category,
// optionally narrow by year range + title search; the server subtracts what is
// already transcribed and a durable saga transcribes the remainder ONE video at
// a time (YT rate limits), with live progress and a Stop that takes effect
// between videos. No project needed — transcripts attach to their channel;
// indexing into a project is the separate Batch-index bar above.
const BULK_CAPTION_MODES = [
  ['use', 'Captions where available (ASR fallback)'],
  ['only', 'Captions only (skip ASR)'],
  ['skip', 'Always transcribe (ignore captions)'],
]

function BatchTranscribeBar({ channel, category }) {
  const [years, setYears] = useState({ from: '', to: '' })
  const [search, setSearch] = useState('')
  const [captions, setCaptions] = useState('use')
  const [keepVideo, setKeepVideo] = useState(false)
  const [audioClean, setAudioClean] = useState(false)
  const [cleanup, setCleanup] = useState(false)
  const [speedPreset, setSpeedPreset] = useState('')
  const [force, setForce] = useState(false)
  const [showOpts, setShowOpts] = useState(false)
  const [preview, setPreview] = useState(null)  // { not_transcribed, matched, skipped_undated, sample }
  const [sagaId, setSagaId] = useState(null)

  const previewM = useTranscribeBatchPreview()
  const startM = useTranscribeBatchStart()
  const cancelM = useCancelSaga()
  const saga = useSaga(sagaId)

  const yearRange = () => {
    const f = years.from ? Number(years.from) : null
    const t = years.to ? Number(years.to) : null
    return f == null && t == null ? undefined : [f, t]
  }
  const scopeArgs = { channel_id: channel || undefined, category: channel ? undefined : category }
  const filterArgs = () => ({ ...scopeArgs, years: yearRange(), search: search.trim() || undefined })

  // filter changes invalidate a stale preview
  useEffect(() => { setPreview(null) }, [channel, category, years.from, years.to, search])

  const doPreview = () => previewM.mutate(filterArgs(), { onSuccess: setPreview })
  const doStart = () =>
    startM.mutate(
      { ...filterArgs(), options: { captions, keep_video: keepVideo, audio_clean: audioClean, cleanup, speed_preset: speedPreset || undefined, force } },
      { onSuccess: (r) => { setSagaId(r.saga_id); setPreview(null) } }
    )

  const status = saga.data?.status
  const done = status && SAGA_TERMINAL.has(status)
  const running = sagaId && !done
  const steps = saga.data?.steps ?? []
  const resolveStep = steps.find((s) => s.name === 'resolve')
  const txStep = steps.find((s) => s.name === 'transcribe')
  const label = channel ? `channel ${channel}` : `category ${category}`
  const inputCls = 'h-8 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist disabled:opacity-50'

  return (
    <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/[0.04] dark:border-cyan-400/20 dark:bg-cyan-400/[0.03] px-4 py-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Mic className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0" />
        <span className="text-ink dark:text-mist">Bulk transcribe <span className="font-medium">{label}</span></span>

        <label className="inline-flex items-center gap-1 text-xs text-ink-soft dark:text-mist/60">
          Years
          <input type="number" placeholder="from" value={years.from} disabled={running}
            onChange={(e) => setYears((y) => ({ ...y, from: e.target.value }))}
            className={`${inputCls} w-16`} />
          <span>–</span>
          <input type="number" placeholder="to" value={years.to} disabled={running}
            onChange={(e) => setYears((y) => ({ ...y, to: e.target.value }))}
            className={`${inputCls} w-16`} />
        </label>
        <input type="text" placeholder="title contains…" value={search} disabled={running}
          onChange={(e) => setSearch(e.target.value)} className={`${inputCls} w-36`} />

        <button type="button" onClick={() => setShowOpts((v) => !v)} disabled={running}
          className={`${inputCls} inline-flex items-center gap-1 disabled:opacity-50`} title="Transcription options">
          <SlidersHorizontal className="w-3.5 h-3.5" /> Options
        </button>

        <div className="ml-auto inline-flex items-center gap-2">
          {!preview && !running && !done && (
            <button onClick={doPreview} disabled={previewM.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-cyan-500/15 text-cyan-700 hover:bg-cyan-500/25 dark:text-cyan-300 disabled:opacity-50">
              {previewM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
              {previewM.isPending ? 'Counting…' : 'Preview'}
            </button>
          )}
          {running && (
            <button onClick={() => cancelM.mutate(sagaId)} disabled={cancelM.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20 disabled:opacity-50">
              <StopCircle className="w-3.5 h-3.5" /> Stop
            </button>
          )}
        </div>
      </div>

      {showOpts && !running && (
        <div className="flex flex-wrap items-center gap-4 text-xs pt-1 border-t border-cyan-500/15">
          <label className="inline-flex items-center gap-1.5 text-ink-soft dark:text-mist/60">
            <select value={captions} onChange={(e) => setCaptions(e.target.value)} className={inputCls}>
              {BULK_CAPTION_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-1.5 text-ink dark:text-mist">
            <input type="checkbox" className="accent-cyan-500" checked={keepVideo} onChange={(e) => setKeepVideo(e.target.checked)} /> keep video
          </label>
          <label className="inline-flex items-center gap-1.5 text-ink dark:text-mist">
            <input type="checkbox" className="accent-cyan-500" checked={audioClean} onChange={(e) => setAudioClean(e.target.checked)} /> denoise audio
          </label>
          <label className="inline-flex items-center gap-1.5 text-ink dark:text-mist">
            <input type="checkbox" className="accent-cyan-500" checked={cleanup} onChange={(e) => setCleanup(e.target.checked)} /> LLM cleanup
          </label>
          <label className="inline-flex items-center gap-1.5 text-ink-soft dark:text-mist/60">
            Speed
            <select value={speedPreset} onChange={(e) => setSpeedPreset(e.target.value)} className={inputCls}>
              <option value="">default</option>
              <option value="fast">fast</option>
              <option value="balanced">balanced</option>
              <option value="quality">quality</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-1.5 text-ink dark:text-mist" title="Re-transcribe even if a transcript already exists">
            <input type="checkbox" className="accent-cyan-500" checked={force} onChange={(e) => setForce(e.target.checked)} /> force
          </label>
        </div>
      )}

      {previewM.isError && (
        <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {previewM.error?.message}</p>
      )}

      {/* delta preview → confirm */}
      {preview && !running && !done && (
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-ink-soft dark:text-mist/60">
            <b className="text-ink dark:text-mist">{preview.not_transcribed}</b> not transcribed ·{' '}
            <b className="text-cyan-700 dark:text-cyan-300">{preview.matched}</b> match this filter
            {preview.skipped_undated > 0 && <> · {preview.skipped_undated} undated (excluded by year)</>}
          </span>
          <button onClick={doStart} disabled={preview.matched === 0 || startM.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed">
            {startM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
            {preview.matched === 0 ? 'Nothing to transcribe' : `Transcribe ${preview.matched}`}
          </button>
          <button onClick={() => setPreview(null)} className="text-ink-soft hover:text-ink dark:text-mist/50">dismiss</button>
        </div>
      )}

      {/* live saga progress */}
      {sagaId && (
        <div className="text-xs space-y-1">
          {!done && resolveStep?.status !== 'done' && (
            <p className="text-ink-soft dark:text-mist/60 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving scope…</p>
          )}
          {(running || done) && txStep && (() => {
            const d = txStep.data ?? {}
            const total = d.total ?? resolveStep?.data?.total ?? 0
            const cur = (d.transcribed ?? 0) + (d.failed?.length ?? d.failed ?? 0)
            const pct = total ? Math.round((cur / total) * 100) : (done ? 100 : 0)
            return (
              <>
                <div className="h-1.5 rounded-full bg-parchment-border dark:bg-midnight-600 overflow-hidden">
                  <div className="h-full bg-cyan-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-ink-soft dark:text-mist/60">
                  {done ? 'Done — ' : `Transcribing ${cur + 1}/${total}${d.phase ? ` (${d.phase})` : '…'} · `}
                  transcribed <b className="text-ink dark:text-mist">{d.transcribed ?? 0}</b>
                  {(d.partial?.length ?? d.partial ?? 0) > 0 && <> · <span className="text-amber-600" title="core transcript OK, a post-phase (TTS/translate/podcast) failed">partial {d.partial?.length ?? d.partial}</span></>}
                  {(d.failed?.length ?? d.failed ?? 0) > 0 && <> · <span className="text-red-600">failed {d.failed?.length ?? d.failed}</span></>}
                </p>
              </>
            )
          })()}
          {status === 'cancelled' && <p className="text-ink-soft dark:text-mist/50">Stopped — the in-flight video finishes; re-preview to see what remains.</p>}
          {status === 'failed' && <p className="text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {saga.data?.error}</p>}
          {done && (
            <button onClick={() => setSagaId(null)} className="text-cyan-700 dark:text-cyan-300 hover:underline">dismiss</button>
          )}
        </div>
      )}
    </div>
  )
}

// Index a transcribed video's subtitles into the Sources-selected RAG project —
// the bridge from "channel content" to "searchable research material".
function IndexVideoButton({ video, project }) {
  const idx = useIndexVideo()

  if (idx.isSuccess) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300"
        title={`Indexed into ${project} (${idx.data?.chunks_indexed ?? '?'} chunks)`}
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> indexed
      </span>
    )
  }
  return (
    <button
      onClick={() => idx.mutate({ video: video.id || video.title, project })}
      disabled={!project || idx.isPending}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
      title={
        idx.isError
          ? idx.error?.message
          : project
            ? `Index the subtitles into project "${project}" (timestamped chunks)`
            : 'Pick a project in Library/Add first'
      }
    >
      {idx.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />}
      {idx.isError ? 'retry index' : project ? `Index → ${project}` : 'Index'}
    </button>
  )
}

const MODEL_SIZES = ['tiny', 'base', 'small', 'medium', 'large-v3']
const PROVIDERS = ['whisper', 'google', 'gemini', 'deepgram', 'assemblyai']
const CAPTION_MODES = [
  ['use', 'Use captions when available'],
  ['skip', 'Skip captions (always STT)'],
  ['only', 'Captions only (no STT)'],
]

// Queue a not-yet-transcribed channel video. Progressive disclosure:
// click = run with the Settings defaults (shown in the tooltip); ▾ = per-run
// overrides popover; "Full pipeline…" = the Media wizard prefilled with this
// video (translate/TTS/cleanup live there).
function TranscribeButton({ video, channel, onFullPipeline }) {
  const qc = useQueryClient()
  const submit = useSubmitImport()
  const [jobId, setJobId] = useState(null)
  const jobQ = useImportJob(jobId)
  const status = jobQ.data?.status
  const [menuOpen, setMenuOpen] = useState(false)
  // caption fast-path: transcribe_video completes synchronously with NO job_id —
  // track that outcome locally so the row doesn't end the spinner in silence
  const [syncState, setSyncState] = useState(null) // 'done' | 'error:<msg>'
  const defaults = useDefaults().data?.transcribe
  // per-run overrides, seeded from defaults when the popover opens
  const [ov, setOv] = useState(null)

  const defSummary = [
    defaults?.provider || 'whisper',
    defaults?.model_size,
    defaults?.language || 'auto',
  ]
    .filter(Boolean)
    .join(' · ')

  const start = (overrides) => {
    setMenuOpen(false)
    setSyncState(null)
    submit.mutate(
      { input: video.id || video.title, channel_id: channel, ...(overrides || {}) },
      {
        onSuccess: (r) => {
          if (r?.job_id) return setJobId(r.job_id)
          if (r?.status === 'completed' || r?.status === 'already_transcribed') {
            setSyncState('done')
            qc.invalidateQueries({ queryKey: ['transcripts', 'videos'] })
            qc.invalidateQueries({ queryKey: ['transcripts', 'channels'] })
          } else {
            setSyncState(`error:${r?.error || r?.message || `unexpected status '${r?.status}'`}`)
          }
        },
      }
    )
  }

  const openMenu = () => {
    setOv({
      provider: defaults?.provider || 'whisper',
      model_size: defaults?.model_size || '',
      language: defaults?.language || '',
      captions: 'use',
    })
    setMenuOpen(true)
  }

  // When the job lands, flip the row (badge + View transcript) by refetching.
  useEffect(() => {
    if (status && TERMINAL.has(status)) {
      qc.invalidateQueries({ queryKey: ['transcripts', 'videos'] })
      qc.invalidateQueries({ queryKey: ['transcripts', 'channels'] })
    }
  }, [status, qc])

  if (jobId && status && !TERMINAL.has(status)) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gold-deep dark:text-gold">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {jobQ.data?.progress_percent != null ? `${Math.round(jobQ.data.progress_percent)}%` : 'queued'}
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-red-600" title={jobQ.data?.error}>
        failed
        <button onClick={() => setJobId(null)} className="underline hover:text-red-700">retry</button>
      </span>
    )
  }
  if ((status && TERMINAL.has(status)) || syncState === 'done') {
    // completed/already_transcribed — the refetch above re-renders the row.
    // Partial: transcript OK but a post-phase (tts/translate/podcast) failed —
    // don't read as a clean success (plan-transcribe-parity Step 3).
    const failedPhases = jobQ.data?.failed_phases
    if (Array.isArray(failedPhases) && failedPhases.length) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-amber-600" title={`These post-phases failed: ${failedPhases.join(', ')}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> partial ({failedPhases.join(', ')})
        </span>
      )
    }
    return <span className="text-xs text-emerald-700 dark:text-emerald-300">done</span>
  }
  if (submit.isError || syncState?.startsWith('error:')) {
    const msg = submit.isError ? submit.error?.message : syncState.slice(6)
    return (
      <span className="inline-flex items-center gap-2 text-xs text-red-600" title={msg}>
        failed
        <button
          onClick={() => {
            submit.reset()
            setSyncState(null)
          }}
          className="underline hover:text-red-700"
        >
          retry
        </button>
      </span>
    )
  }
  return (
    <div className="relative">
      <div className="inline-flex rounded-md border border-parchment-border dark:border-midnight-600 overflow-hidden">
        <button
          onClick={() => start()}
          disabled={submit.isPending}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-ink-soft hover:bg-gold/10 hover:text-gold-deep disabled:opacity-40 dark:text-mist/60 dark:hover:text-gold"
          title={`Transcribe with your defaults: ${defSummary}${defaults?.audio_clean ? ' · audio clean' : ''}${defaults?.cleanup ? ' · cleanup' : ''} (change them in Settings)`}
        >
          {submit.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
          Transcribe
        </button>
        <button
          onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
          disabled={submit.isPending}
          className="px-1.5 py-1 text-xs border-l border-parchment-border text-ink-soft hover:bg-gold/10 hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
          aria-label="Transcription options"
          title="Options for this run"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>

      {menuOpen && ov && (
        <>
          {/* click-away backdrop */}
          <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 w-64 rounded-xl border border-gold/40 bg-parchment-50 p-3 shadow-2xl dark:border-gold/30 dark:bg-midnight-800 space-y-2.5">
            <div className="text-[11px] uppercase tracking-[0.14em] text-gold-deep dark:text-gold">This run only</div>
            <label className="block text-xs text-ink-soft dark:text-mist/60">
              Provider
              <select
                value={ov.provider}
                onChange={(e) => setOv({ ...ov, provider: e.target.value })}
                className="mt-0.5 w-full h-8 rounded-md px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              >
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </label>
            {ov.provider === 'whisper' && (
              <label className="block text-xs text-ink-soft dark:text-mist/60">
                Model size
                <select
                  value={ov.model_size}
                  onChange={(e) => setOv({ ...ov, model_size: e.target.value })}
                  className="mt-0.5 w-full h-8 rounded-md px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
                >
                  <option value="">default</option>
                  {MODEL_SIZES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="block text-xs text-ink-soft dark:text-mist/60">
              Spoken language
              <input
                value={ov.language}
                onChange={(e) => setOv({ ...ov, language: e.target.value })}
                placeholder="auto-detect"
                className="mt-0.5 w-full h-8 rounded-md px-2 text-xs bg-white border border-parchment-border text-ink placeholder:text-ink-soft/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              />
            </label>
            <label className="block text-xs text-ink-soft dark:text-mist/60">
              Captions
              <select
                value={ov.captions}
                onChange={(e) => setOv({ ...ov, captions: e.target.value })}
                className="mt-0.5 w-full h-8 rounded-md px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              >
                {CAPTION_MODES.map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </label>
            <button
              onClick={() =>
                start({
                  provider: ov.provider,
                  model_size: ov.model_size || undefined,
                  language: ov.language.trim() || undefined,
                  captions: ov.captions,
                })
              }
              className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md text-xs border border-gold/50 text-gold-deep hover:bg-gold/10 dark:text-gold"
            >
              <Mic className="w-3.5 h-3.5" /> Transcribe with these
            </button>
            {onFullPipeline && (
              <button
                onClick={() => {
                  setMenuOpen(false)
                  onFullPipeline({
                    channel_id: channel,
                    video_id: video.id,
                    title: video.title,
                    origin: video.origin_type,
                  })
                }}
                className="w-full text-left text-xs text-ink-soft hover:text-gold-deep dark:text-mist/60 dark:hover:text-gold underline underline-offset-2"
                title="Translate, TTS, audio clean and more — opens the Media wizard with this video"
              >
                Full pipeline… (translate, TTS, cleanup)
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

const hmsToSec = (t) => {
  const [h, m, s] = t.split(':').map(Number)
  return h * 3600 + m * 60 + s
}

// Parse raw SRT into readable blocks: cues grouped into ~groupSec windows, each
// block labeled with its time range (a deep link when the video URL is known).
function parseSrtBlocks(srt, groupSec = 90) {
  const cues = []
  const re = /(\d{2}:\d{2}:\d{2})[,.]\d{3}\s*-->\s*(\d{2}:\d{2}:\d{2})[,.]\d{3}\s*\n([\s\S]*?)(?=\n\s*\n|$)/g
  let m
  while ((m = re.exec(srt))) {
    const text = m[3].trim()
    if (text) cues.push({ start: m[1], end: m[2], startSec: hmsToSec(m[1]), text })
  }
  const blocks = []
  for (const c of cues) {
    const last = blocks[blocks.length - 1]
    if (!last || c.startSec - last.startSec >= groupSec) {
      blocks.push({ start: c.start, end: c.end, startSec: c.startSec, lines: [c.text] })
    } else {
      last.lines.push(c.text)
      last.end = c.end
    }
  }
  return blocks
}

function SrtView({ srt, linkMeta }) {
  const openPlayer = usePlayerStore((s) => s.openPlayer)
  const blocks = parseSrtBlocks(srt)
  const labelCls = 'mb-1.5 inline-block font-mono text-[11px] tracking-wide text-gold-deep dark:text-gold/80'
  const embeddable = canEmbed(linkMeta)
  return (
    <div className="text-[14px] leading-7 text-ink/90 dark:text-mist/90">
      {blocks.map((b, i) => {
        const href = sourceLink(linkMeta, b.startSec)
        const label = `${b.start} – ${b.end}`
        return (
          <div key={i} className="mb-4">
            {embeddable ? (
              <button
                onClick={() => openPlayer({ ...linkMeta, start: b.startSec })}
                title="Watch the source at this moment"
                className={`${labelCls} hover:underline`}
              >
                ▶ {label}
              </button>
            ) : href ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                title="Open the video at this moment"
                className={`${labelCls} hover:underline`}
              >
                ▶ {label}
              </a>
            ) : (
              <div className={labelCls}>{label}</div>
            )}
            <div className="whitespace-pre-wrap break-words border-l-2 border-parchment-border dark:border-midnight-600 pl-3">
              {b.lines.join('\n')}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function TranscriptPanel({ video, onClose }) {
  const [format, setFormat] = useState('text')
  const q = useTranscript(video.id, format)
  const content = q.data?.content ?? ''
  // provenance for deep links + the embedded player (from get_transcript metadata)
  const linkMeta = {
    source_url: q.data?.metadata?.video_url,
    origin: q.data?.metadata?.origin_type,
    video_id: q.data?.metadata?.id,
    title: q.data?.video_title || video.title,
  }

  // Close on Esc (it's a modal overlay — with long video lists an inline panel
  // at the bottom of the page was invisible when opened).
  React.useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const onExport = () => {
    const ext = format === 'srt' ? 'srt' : 'txt'
    const safe = (q.data?.video_title || video.title || 'transcript').replace(/[^\w .-]/g, '_').slice(0, 80)
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${safe}.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-midnight-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <section
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[86vh] flex flex-col rounded-xl border border-gold/40 bg-parchment-50 p-5 shadow-2xl dark:border-gold/30 dark:bg-midnight-800"
      >
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-display text-base text-ink dark:text-mist truncate" title={video.title}>{video.title}</h3>
        <div className="flex items-center gap-2 shrink-0">
          <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
            {FORMATS.map(([f, label]) => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                className={`px-2.5 py-1 rounded-md text-xs transition ${
                  format === f ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button onClick={onExport} disabled={!content} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:text-ink hover:border-gold dark:border-midnight-600 dark:text-mist/60 disabled:opacity-40">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          <button onClick={onClose} className="p-1 rounded text-ink-soft hover:text-ink dark:text-mist/50" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {q.isLoading && (
        <div className="flex items-center gap-2 text-sm text-ink-soft dark:text-mist/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading transcript…
        </div>
      )}
      {q.isError && <p className="text-sm text-red-600">{q.error?.message}</p>}
      {!q.isLoading && (
        <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-parchment-border bg-white/60 p-4 dark:border-midnight-600 dark:bg-midnight-900/40">
          {!content ? (
            <span className="text-ink-soft dark:text-mist/50 italic">Empty transcript.</span>
          ) : format === 'srt' ? (
            <SrtView srt={content} linkMeta={linkMeta} />
          ) : format === 'text' ? (
            // transcripts are a wall of sentences — reflow into paragraphs
            <div className="max-w-[68ch] text-[15px] leading-7 text-ink/90 dark:text-mist/90">
              {toParagraphs(content).map((p, i) => (
                <p key={i} className="mb-3 break-words">{p}</p>
              ))}
            </div>
          ) : (
            <div className="text-xs font-mono whitespace-pre-wrap text-ink-soft dark:text-mist/70">{content}</div>
          )}
        </div>
      )}
      </section>
    </div>
  )
}
